import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminOverview() {
  const [kpis, setKpis] = useState(null);
  const [todayFeed, setTodayFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickGrantModal, setShowQuickGrantModal] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const [kpisRes, feedRes] = await Promise.all([
        axios.get(`${API}/admin/overview/kpis`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/overview/today-feed`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setKpis(kpisRes.data);
      setTodayFeed(feedRes.data.events || []);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(
        `${API}/admin/users?search=${query}&limit=5`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleQuickGrant = async () => {
    if (!selectedUserId || !grantAmount || !grantReason) {
      toast.error("Please fill all fields");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUserId}/grant-credits`,
        { amount: parseInt(grantAmount), reason: grantReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Granted ${grantAmount} credits successfully`);
      setShowQuickGrantModal(false);
      setUserSearch("");
      setSearchResults([]);
      setSelectedUserId(null);
      setGrantAmount("");
      setGrantReason("");
      fetchDashboardData(); // Refresh dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant credits");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: "MRR",
      value: `$${kpis?.mrr?.toLocaleString() || "0"}`,
      subtitle: "Monthly Recurring Revenue",
      icon: "💰",
      color: "from-green-500 to-emerald-600",
    },
    {
      title: "Active Subscriptions",
      value: kpis?.active_subscriptions || "0",
      subtitle: "Paying customers",
      icon: "👥",
      color: "from-blue-500 to-cyan-600",
    },
    {
      title: "New Users (30d)",
      value: kpis?.new_users_30d || "0",
      subtitle: `Total: ${kpis?.total_users || "0"}`,
      icon: "✨",
      color: "from-purple-500 to-pink-600",
    },
    {
      title: "Churn Rate",
      value: `${kpis?.churn_rate?.toFixed(1) || "0"}%`,
      subtitle: "Last 30 days",
      icon: "📉",
      color: "from-orange-500 to-red-600",
    },
    {
      title: "Credits Used (30d)",
      value: kpis?.credits_used_30d || "0",
      subtitle: "Total credits consumed",
      icon: "🎟️",
      color: "from-teal-500 to-cyan-600",
    },
    {
      title: "Questions Asked (30d)",
      value: kpis?.questions_asked_30d || "0",
      subtitle: "AI queries",
      icon: "💬",
      color: "from-indigo-500 to-purple-600",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Overview</h1>
          <p className="text-muted-foreground mt-1">Dashboard home • KPIs and recent activity</p>
        </div>
        <div className="text-sm text-muted-foreground">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpiCards.map((card, idx) => (
          <div
            key={idx}
            className="bg-card backdrop-blur-sm border border-border rounded-xl p-6 hover:border-border transition"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center text-2xl`}>
                {card.icon}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm">{card.title}</p>
              <p className="text-3xl font-bold text-foreground">{card.value}</p>
              <p className="text-muted-foreground text-xs">{card.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Today Feed */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Today's Activity</h2>
          <span className="px-3 py-1 bg-primary/10 border border-teal-500/20 rounded-full text-teal-400 text-sm">
            {todayFeed.length} events
          </span>
        </div>

        {todayFeed.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-muted-foreground">No activity today yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todayFeed.map((event, idx) => {
              const getEventStyle = (type) => {
                switch (type) {
                  case "signup":
                    return { icon: "👤", color: "bg-green-500/10 text-green-400 border-green-500/20" };
                  case "upgrade":
                    return { icon: "⬆️", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" };
                  case "payment_failed":
                    return { icon: "⚠️", color: "bg-red-500/10 text-red-400 border-red-500/20" };
                  default:
                    return { icon: "📌", color: "bg-slate-500/10 text-muted-foreground border-slate-500/20" };
                }
              };

              const style = getEventStyle(event.type);

              return (
                <div
                  key={idx}
                  className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg border border-border/50 hover:border-slate-600 transition"
                >
                  <div className={`w-10 h-10 rounded-lg border ${style.color} flex items-center justify-center text-lg shrink-0`}>
                    {style.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground font-medium">{event.message}</p>
                    <p className="text-muted-foreground text-sm truncate">{event.user_email}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  {event.metadata?.plan && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded capitalize">
                      {event.metadata.plan}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => setShowQuickGrantModal(true)}
            className="flex items-center gap-3 p-4 bg-muted hover:bg-muted rounded-lg transition text-left"
          >
            <div className="w-10 h-10 bg-primary/10 border border-teal-500/20 rounded-lg flex items-center justify-center text-teal-400">
              🎟️
            </div>
            <div>
              <p className="text-foreground font-medium">Grant Credits</p>
              <p className="text-muted-foreground text-xs">Add credits to user</p>
            </div>
          </button>
          <button 
            onClick={() => toast.info("Navigate to Users page to reset trials")}
            className="flex items-center gap-3 p-4 bg-muted hover:bg-muted rounded-lg transition text-left"
          >
            <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center text-blue-400">
              🔄
            </div>
            <div>
              <p className="text-foreground font-medium">Reset Trial</p>
              <p className="text-muted-foreground text-xs">Go to Users page</p>
            </div>
          </button>
          <button 
            onClick={() => toast.info("Webhook monitoring coming in Section 5 - Billing")}
            className="flex items-center gap-3 p-4 bg-muted hover:bg-muted rounded-lg transition text-left"
          >
            <div className="w-10 h-10 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
              📊
            </div>
            <div>
              <p className="text-foreground font-medium">View Webhooks</p>
              <p className="text-muted-foreground text-xs">Coming in Section 5</p>
            </div>
          </button>
        </div>
      </div>

      {/* Quick Grant Credits Modal */}
      {showQuickGrantModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">Quick Grant Credits</h3>
            
            {/* User Search */}
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Search User</label>
              <input
                type="text"
                placeholder="Type email or name..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  searchUsers(e.target.value);
                }}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-muted border border-border rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUserId(user.id);
                        setUserSearch(`${user.name} (${user.email})`);
                        setSearchResults([]);
                      }}
                      className="w-full p-3 text-left hover:bg-muted transition border-b border-border last:border-0"
                    >
                      <p className="text-foreground text-sm font-medium">{user.name}</p>
                      <p className="text-muted-foreground text-xs">{user.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Amount</label>
              <input
                type="number"
                placeholder="Number of credits"
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Reason</label>
              <input
                type="text"
                placeholder="Reason for granting credits"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleQuickGrant}
                disabled={actionLoading || !selectedUserId}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition"
              >
                {actionLoading ? "Processing..." : "Grant Credits"}
              </button>
              <button
                onClick={() => {
                  setShowQuickGrantModal(false);
                  setUserSearch("");
                  setSearchResults([]);
                  setSelectedUserId(null);
                  setGrantAmount("");
                  setGrantReason("");
                }}
                className="flex-1 px-4 py-2 bg-muted hover:bg-slate-600 text-foreground rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
